/**
 * Question and content type definitions
 * Based on durak_history.html question corpus and perspective challenges
 */

import type { Rank, Difficulty } from './game';

/** Historical era categories */
export type Era =
  | 'Kievan Rus'
  | 'Early Medieval'
  | 'GDL'
  | 'Polish-Lithuanian Commonwealth'
  | 'Russian Imperial'
  | '19th Century'
  | 'Interwar'
  | 'Soviet'
  | 'Modern Historiography';

/** Question structure with multi-perspective views */
export interface Question {
  /** Unique identifier */
  id: string;

  /** Historical era category */
  era: Era;

  /** Difficulty tier */
  difficulty: Difficulty;

  /** Associated card rank (for difficulty mapping) */
  card: Rank;

  /** Question text (English – source language) */
  text: string;

  /** Multiple choice options (English) */
  options: string[];

  /** Index of correct answer in options array */
  correct: number;

  /** Educational feedback shown after answering (English) */
  feedback: string;

  /** Lithuanian historical perspective */
  lt_view: string;

  /** Belarusian historical perspective */
  be_view: string;

  /** Neutral/academic consensus view */
  neutral: string;

  /** Source citation for academic credibility */
  source: string;

  /** Optional Lithuanian translation of question text */
  text_lt?: string;
  /** Optional Belarusian translation of question text */
  text_be?: string;
  /** Optional Lithuanian translation of options */
  options_lt?: string[];
  /** Optional Belarusian translation of options */
  options_be?: string[];
  /** Optional Lithuanian translation of feedback */
  feedback_lt?: string;
  /** Optional Belarusian translation of feedback */
  feedback_be?: string;
}

/** Resolve a question's text for a given language with fallback to English */
export function resolveQuestionText(q: Question, lang: 'en' | 'lt' | 'be'): string {
  if (lang === 'lt' && q.text_lt) return q.text_lt;
  if (lang === 'be' && q.text_be) return q.text_be;
  return q.text;
}

/** Resolve question options for a given language with fallback to English */
export function resolveQuestionOptions(q: Question, lang: 'en' | 'lt' | 'be'): string[] {
  if (lang === 'lt' && q.options_lt && q.options_lt.length === q.options.length) return q.options_lt;
  if (lang === 'be' && q.options_be && q.options_be.length === q.options.length) return q.options_be;
  return q.options;
}

/** Resolve question feedback for a given language with fallback to English */
export function resolveQuestionFeedback(q: Question, lang: 'en' | 'lt' | 'be'): string {
  if (lang === 'lt' && q.feedback_lt) return q.feedback_lt;
  if (lang === 'be' && q.feedback_be) return q.feedback_be;
  return q.feedback;
}

/** Extended question with answer tracking */
export interface AnsweredQuestion extends Question {
  /** Whether the answer was correct */
  wasCorrect: boolean;

  /** XP gained (0 if wrong) */
  xpGain: number;

  /** Timestamp when answered */
  answeredAt: number;
}

/** Perspective challenge for trump cards */
export interface Perspective {
  /** Topic being discussed */
  topic: string;

  /** Lithuanian perspective text */
  lt: string;

  /** Belarusian perspective text */
  be: string;

  /** Lithuanian source citation */
  ltSource: string;

  /** Belarusian source citation */
  beSource: string;

  /** Neutral academic interpretation */
  neutral: string;

  /** Quoted source for verification challenge */
  sourceQuote: string;

  /** Correct answer: 0=LT, 1=BE, 2=Neutral */
  correctAnswer: 0 | 1 | 2;
}

/** SRS (Spaced Repetition System) card state */
export interface SRSCard {
  /** Reference to question ID */
  questionId: string;

  /** Current interval in days */
  interval: number;

  /** Number of successful reviews */
  repetitions: number;

  /** Ease factor (default 2.5) */
  easeFactor: number;

  /** Next scheduled review date */
  nextReview: Date;

  /** Last rating given */
  lastRating?: 'again' | 'hard' | 'good' | 'easy';
}

/** SRS rating options */
export type SRSRating = 'again' | 'hard' | 'good' | 'easy';

/** SRS schedule intervals for new cards */
export const SRS_INTERVALS: Record<SRSRating, string> = {
  again: '1 day',
  hard: '2 days',
  good: '4 days',
  easy: '7 days',
} as const;

/** Question filter options */
export interface QuestionFilter {
  era?: Era;
  difficulty?: Difficulty;
  excludeIds?: string[];
}

/** Content validation result */
export interface ContentValidationResult {
  valid: boolean;
  errors: string[];
}
